import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyRestOptions } from 'shared/cex-sdk';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { bootstrapStrategy } from '../bootstrap';
import { aggregateOrderbook } from '../utils';

const BITHUMB_REST_BASE = 'https://api.bithumb.com';
const BITHUMB_WS_URL = 'wss://ws-api.bithumb.com/websocket/v1';
const BITHUMB_MAX_CANDLES_PER_REQUEST = 200;
const BITHUMB_MAX_HISTORY = 300;
const BITHUMB_MAX_TRADES = 200;
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

export const bithumbRestClient = proxyRestOptions.client!;

type BithumbNumber = number | string;

interface BithumbErrorEnvelope {
  error: {
    name?: number | string;
    message?: string;
  };
}

interface BithumbTicker {
  market?: string;
  trade_price: BithumbNumber;
  high_price: BithumbNumber;
  low_price: BithumbNumber;
  signed_change_rate: BithumbNumber;
  acc_trade_volume_24h: BithumbNumber;
  acc_trade_price_24h: BithumbNumber;
}

interface BithumbOrderbookUnit {
  ask_price: BithumbNumber;
  bid_price: BithumbNumber;
  ask_size: BithumbNumber;
  bid_size: BithumbNumber;
}

interface BithumbOrderbook {
  market?: string;
  timestamp: number;
  orderbook_units: BithumbOrderbookUnit[];
}

export interface BithumbCandle {
  market?: string;
  candle_date_time_utc: string;
  opening_price: BithumbNumber;
  high_price: BithumbNumber;
  low_price: BithumbNumber;
  trade_price: BithumbNumber;
  candle_acc_trade_volume: BithumbNumber;
}

interface BithumbTrade {
  market?: string;
  code?: string;
  trade_price: BithumbNumber;
  trade_volume: BithumbNumber;
  timestamp?: number;
  trade_timestamp?: number;
  ask_bid: string;
}

type BithumbWsMessage =
  | ({ type: 'ticker'; code: string } & BithumbTicker)
  | ({ type: 'orderbook'; code: string } & BithumbOrderbook)
  | ({ type: 'trade'; code: string } & BithumbTrade)
  | BithumbErrorEnvelope;

const BITHUMB_CANDLE_PATHS: Partial<Record<IChartTimeframe, string>> = {
  '1m': '/v1/candles/minutes/1',
  '3m': '/v1/candles/minutes/3',
  '5m': '/v1/candles/minutes/5',
  '15m': '/v1/candles/minutes/15',
  '30m': '/v1/candles/minutes/30',
  '1h': '/v1/candles/minutes/60',
  '4h': '/v1/candles/minutes/240',
  '24h': '/v1/candles/days',
  '1d': '/v1/candles/days',
  '1w': '/v1/candles/weeks',
  '168h': '/v1/candles/weeks',
  '1M': '/v1/candles/months',
};

const asString = (value: BithumbNumber) => String(value);

const utcCandleTimestamp = (value: string): number => {
  const timestamp = Date.parse(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`);
  if (!Number.isFinite(timestamp)) {
    throw new Error(`Invalid Bithumb candle UTC timestamp: ${value}`);
  }
  return timestamp;
};

const observedOrderbookTick = (units: BithumbOrderbookUnit[]): string | undefined => {
  const prices = units
    .flatMap(unit => [Number(unit.bid_price), Number(unit.ask_price)])
    .filter(Number.isFinite)
    .sort((left, right) => left - right);

  let smallest = Number.POSITIVE_INFINITY;
  for (let index = 1; index < prices.length; index += 1) {
    const difference = prices[index] - prices[index - 1];
    if (difference > 0 && difference < smallest) smallest = difference;
  }
  if (!Number.isFinite(smallest)) return undefined;
  return String(Number(smallest.toPrecision(12)));
};

export const unwrapBithumbPayload = <T>(payload: T | BithumbErrorEnvelope, resource: string): T => {
  if (payload && typeof payload === 'object' && 'error' in payload && payload.error?.message) {
    throw new Error(`Bithumb ${resource}: ${payload.error.message}`);
  }
  return payload as T;
};

export const buildBithumbMarketCode = (base: string, quote: string) =>
  `${quote.toUpperCase()}-${base.toUpperCase()}`;

export const normalizeBithumbCandles = (candles: BithumbCandle[]): KLine[] =>
  candles
    .map(
      candle =>
        [
          utcCandleTimestamp(candle.candle_date_time_utc),
          asString(candle.opening_price),
          asString(candle.high_price),
          asString(candle.low_price),
          asString(candle.trade_price),
          asString(candle.candle_acc_trade_volume),
        ] as KLine,
    )
    .sort((left, right) => left[0] - right[0]);

export const formatBithumbKstCursor = (timestamp: number): string =>
  new Date(timestamp + KST_OFFSET_MS).toISOString().slice(0, 19);

export const bithumbPrecisionFromTick = (tick: string | undefined): number => {
  if (!tick || !Number.isFinite(Number(tick)) || Number(tick) <= 0) return 1;
  const normalized = tick.toLowerCase();
  if (normalized.includes('e-')) {
    const exponent = Number(normalized.split('e-')[1]);
    return Number.isFinite(exponent) ? exponent : 1;
  }
  const [, decimals = ''] = normalized.split('.');
  return decimals.replace(/0+$/, '').length;
};

export const buildBithumbSubscription = (market: string, ticket: string) => [
  { ticket },
  { type: 'ticker', codes: [market] },
  { type: 'trade', codes: [market] },
  { type: 'orderbook', codes: [market] },
  { format: 'DEFAULT' },
];

const readBithumbSocketPayload = async (data: unknown): Promise<string> => {
  if (typeof data === 'string') return data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) return data.text();
  if (
    data instanceof ArrayBuffer ||
    Object.prototype.toString.call(data) === '[object ArrayBuffer]'
  ) {
    return new TextDecoder().decode(data as ArrayBuffer);
  }
  if (ArrayBuffer.isView(data)) {
    return new TextDecoder().decode(new Uint8Array(data.buffer, data.byteOffset, data.byteLength));
  }
  throw new Error('Unsupported Bithumb WebSocket payload');
};

export class BithumbStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pair: string;
  displayDepth: number;
  precision = 1;
  selectedPrecision = 1;
  priceTick = '0.1';
  asks: string[][] = [];
  bids: string[][] = [];
  liveCandle: KLine | undefined;
  liveIntervalMs = 60_000;

  onOrderbookUpdate: (value: IOrderbook) => void;
  onTickerUpdate: (value: ITickerSocket) => void;
  onTradeUpdate: (value: TradeSocket) => void;
  klineUpdate: (value: KLine) => void;
  precisionUpdate: (value: number) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    onTradeUpdate,
    klineUpdate,
    precisionUpdate,
  }: IInitArgs) {
    this.pair = buildBithumbMarketCode(base, quote);
    this.displayDepth = orderbookDepth ?? 30;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.onTradeUpdate = onTradeUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
  }

  publishOrderbook = () => {
    this.onOrderbookUpdate(
      aggregateOrderbook(this.asks, this.bids, this.selectedPrecision, this.displayDepth),
    );
  };

  publishTicker = (ticker: BithumbTicker) => {
    const rawLast = asString(ticker.trade_price);
    const changeNumber = Number(ticker.signed_change_rate) * 100;
    this.onTickerUpdate({
      baseVolume: asString(ticker.acc_trade_volume_24h),
      change: normalizeNumber(Number.isFinite(changeNumber) ? changeNumber : 0),
      close: rawLast,
      high: asString(ticker.high_price),
      last: rawLast,
      low: asString(ticker.low_price),
      quoteVolume: asString(ticker.acc_trade_price_24h),
    });
  };

  applyOrderbook = (book: BithumbOrderbook) => {
    if (!book.orderbook_units.length) {
      throw new Error(`Bithumb orderbook ${this.pair} is empty`);
    }
    this.asks = book.orderbook_units.map(unit => [
      asString(unit.ask_price),
      asString(unit.ask_size),
    ]);
    this.bids = book.orderbook_units.map(unit => [
      asString(unit.bid_price),
      asString(unit.bid_size),
    ]);

    const observedTick = observedOrderbookTick(book.orderbook_units);
    if (observedTick) {
      this.priceTick = observedTick;
      this.precision = bithumbPrecisionFromTick(observedTick);
      this.selectedPrecision = this.precision;
      this.precisionUpdate(this.precision);
    }
    this.publishOrderbook();
  };

  publishTrade = (trade: BithumbTrade) => {
    const timestamp = trade.trade_timestamp ?? trade.timestamp;
    if (!timestamp) throw new Error('Bithumb trade timestamp is missing');
    const price = asString(trade.trade_price);
    const quantity = asString(trade.trade_volume);
    const side = trade.ask_bid.toUpperCase() === 'BID' ? 'buy' : 'sell';

    this.onTradeUpdate({
      price,
      quantity,
      timestamp: String(timestamp),
      side,
    });

    const bucket = Math.floor(timestamp / this.liveIntervalMs) * this.liveIntervalMs;
    if (!this.liveCandle || this.liveCandle[0] !== bucket) {
      this.liveCandle = [bucket, price, price, price, price, quantity];
    } else {
      this.liveCandle = [
        bucket,
        this.liveCandle[1],
        normalizeNumber(Math.max(Number(this.liveCandle[2]), Number(price))),
        normalizeNumber(Math.min(Number(this.liveCandle[3]), Number(price))),
        price,
        normalizeNumber(Number(this.liveCandle[5]) + Number(quantity)),
      ];
    }
    this.klineUpdate(this.liveCandle);
  };

  fetchOrderbook = async () => {
    try {
      const response = await bithumbRestClient.get<BithumbOrderbook[] | BithumbErrorEnvelope>(
        `${BITHUMB_REST_BASE}/v1/orderbook`,
        { params: { markets: this.pair } },
      );
      const books = unwrapBithumbPayload<BithumbOrderbook[]>(
        response.data,
        `orderbook ${this.pair}`,
      );
      const book = books[0];
      if (!book) throw new Error(`Bithumb orderbook ${this.pair} is empty`);
      this.applyOrderbook(book);
    } catch (error) {
      console.log('Bithumb orderbook error:', error);
    }
  };

  fetchTicker = async () => {
    try {
      const response = await bithumbRestClient.get<BithumbTicker[] | BithumbErrorEnvelope>(
        `${BITHUMB_REST_BASE}/v1/ticker`,
        { params: { markets: this.pair } },
      );
      const tickers = unwrapBithumbPayload<BithumbTicker[]>(response.data, `ticker ${this.pair}`);
      const ticker = tickers[0];
      if (!ticker) throw new Error(`Bithumb ticker ${this.pair} is empty`);
      this.publishTicker(ticker);
    } catch (error) {
      console.log('Bithumb ticker error:', error);
    }
  };

  fetchKlines = async ({
    from,
    to,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }): Promise<KLine[]> => {
    const path = BITHUMB_CANDLE_PATHS[interval];
    if (!path) return [];

    const boundedLimit = Math.min(BITHUMB_MAX_HISTORY, Math.max(1, limit));
    const byTimestamp = new Map<number, KLine>();
    let cursor = to > 0 ? to : Date.now();

    try {
      while (byTimestamp.size < boundedLimit) {
        const count = Math.min(BITHUMB_MAX_CANDLES_PER_REQUEST, boundedLimit - byTimestamp.size);
        const response = await bithumbRestClient.get<BithumbCandle[] | BithumbErrorEnvelope>(
          `${BITHUMB_REST_BASE}${path}`,
          {
            params: {
              market: this.pair,
              count,
              to: formatBithumbKstCursor(cursor),
            },
          },
        );
        const candles = unwrapBithumbPayload<BithumbCandle[]>(
          response.data,
          `candles ${this.pair}`,
        );
        if (candles.length === 0) break;

        const normalized = normalizeBithumbCandles(candles);
        normalized.forEach(row => {
          if ((from <= 0 || row[0] >= from) && row[0] < cursor) {
            byTimestamp.set(row[0], row);
          }
        });

        const oldest = normalized[0]?.[0];
        if (!oldest || oldest >= cursor || candles.length < count) break;
        cursor = oldest;
        if (from > 0 && cursor <= from) break;
      }

      const result = [...byTimestamp.values()]
        .sort((left, right) => left[0] - right[0])
        .slice(-boundedLimit);
      if (interval === '1m') this.liveCandle = result.at(-1);
      return result;
    } catch (error) {
      console.log('Bithumb klines error:', error);
      return [];
    }
  };

  fetchTrades = async ({ limit }: { base: string; quote: string; limit: number }) => {
    try {
      const response = await bithumbRestClient.get<BithumbTrade[] | BithumbErrorEnvelope>(
        `${BITHUMB_REST_BASE}/v1/trades/ticks`,
        {
          params: {
            market: this.pair,
            count: Math.min(BITHUMB_MAX_TRADES, Math.max(1, limit)),
          },
        },
      );
      const trades = unwrapBithumbPayload<BithumbTrade[]>(response.data, `trades ${this.pair}`);
      return trades.map(trade => ({
        price: asString(trade.trade_price),
        quantity: asString(trade.trade_volume),
        timestamp: String(trade.trade_timestamp ?? trade.timestamp ?? 0),
        side: trade.ask_bid.toUpperCase() === 'BID' ? 'buy' : 'sell',
      }));
    } catch (error) {
      console.log('Bithumb trades error:', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();
    this.liveIntervalMs = interval;

    const ws = new ReconnectingWebSocket(BITHUMB_WS_URL, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });
    ws.binaryType = 'arraybuffer';
    this.ws = ws;

    ws.onopen = () => {
      this.asks = [];
      this.bids = [];
      ws.send(
        JSON.stringify(
          buildBithumbSubscription(this.pair, `111-dashboard-bithumb-${Date.now().toString(36)}`),
        ),
      );
    };

    ws.onmessage = async event => {
      try {
        const payload = await readBithumbSocketPayload(event.data);
        const message = unwrapBithumbPayload<BithumbWsMessage>(
          JSON.parse(payload) as BithumbWsMessage,
          'WebSocket',
        );

        if ('type' in message && message.type === 'ticker') {
          this.publishTicker(message);
          return;
        }
        if ('type' in message && message.type === 'orderbook') {
          this.applyOrderbook(message);
          return;
        }
        if ('type' in message && message.type === 'trade') {
          this.publishTrade(message);
        }
      } catch (error) {
        console.log('Bithumb websocket error:', error);
      }
    };
  };

  closeWebSocket = () => {
    this.ws?.close();
    this.ws = undefined;
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth = (depth: number) => {
    this.displayDepth = depth;
    this.publishOrderbook();
  };

  getOrderbookTicks = () => [
    {
      value: this.priceTick,
      tick: this.precision,
      id: this.precision,
    },
  ];

  fetch = async () => {
    await Promise.all([this.fetchOrderbook(), this.fetchTicker()]);
  };

  static init = async (args: IInitArgs) => {
    const strategy = new BithumbStrategy(args);
    await bootstrapStrategy(strategy);
    return strategy;
  };
}
